import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import ProfilePictureUpload from "@/components/shared/ProfilePictureUpload";
import LocationCombobox from "@/components/shared/LocationCombobox";
import { PROFILE_TYPES, PROFILE_TYPE_LABEL, GENRES, INSTRUMENTS, GIG_TYPES, VENUE_TYPES, BAND_GROUP_TYPES, MAX_PROFILES_PER_TYPE } from "@/lib/constants";
import { toast } from "sonner";

function ChipGroup({ items, selected, onToggle }) {
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {items.map((v) => (
        <button type="button" key={v} onClick={() => onToggle(v)}
          className={`px-3 py-1 text-sm rounded-full border transition-colors ${
            selected.includes(v) ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white border-slate-200 hover:border-slate-400"
          }`}>
          {v}
        </button>
      ))}
    </div>
  );
}

export default function AddProfile() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const { refresh, profiles } = useAuth();
  const initialType = PROFILE_TYPES.includes(sp.get("type")) ? sp.get("type") : "musician";
  const [type, setType] = useState(initialType);
  const [counts, setCounts] = useState({});
  const [form, setForm] = useState({
    display_name: "", bio: "", profile_picture_url: "",
    venue_type: "", capacity: "", group_type: "Band", members_count: "",
    youtube_url: "", instagram_url: "", soundcloud_url: "", whatsapp_number: "",
  });
  const [location, setLocation] = useState(null);
  const [genres, setGenres] = useState([]);
  const [instruments, setInstruments] = useState([]);
  const [gigTypes, setGigTypes] = useState([]);

  useEffect(() => {
    api.get("/profiles/count-by-type").then(({ data }) => setCounts(data.counts || {})).catch(() => {});
  }, []);

  const toggle = (arr, setArr, v) => setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const profileCounts = useMemo(() => PROFILE_TYPES.reduce((result, profileType) => {
    const loadedCount = profiles.filter((p) => p.profile_type === profileType).length;
    result[profileType] = Math.max(Number(counts[profileType] || 0), loadedCount);
    return result;
  }, {}), [counts, profiles]);
  const availableTypes = useMemo(
    () => PROFILE_TYPES.filter((profileType) => profileCounts[profileType] < MAX_PROFILES_PER_TYPE),
    [profileCounts],
  );
  const limitReached = profileCounts[type] >= MAX_PROFILES_PER_TYPE;

  useEffect(() => {
    if (limitReached && availableTypes.length > 0) setType(availableTypes[0]);
  }, [limitReached, availableTypes, type]);

  // const create = async () => {
  //   if (!form.display_name.trim()) { toast.error("Display name is required"); return; }
  //   if (!location) { toast.error("Location is required"); return; }
  //   if (!form.profile_picture_url) { toast.error("Profile picture is required"); return; }
  //   try {
  //     await api.post("/profiles", {
  //       profile_type: type, display_name: form.display_name.trim(),
  //       location_city: location.city, location_state: location.state,
  //       bio: form.bio,
  //       profile_picture_url: form.profile_picture_url,
  //       genres, instruments, gig_types: gigTypes,
  //       venue_type: type === "venue" ? form.venue_type : null,
  //       capacity: type === "venue" && form.capacity ? Number(form.capacity) : null,
  //       group_type: type === "band" ? (form.group_type || "Band") : null,
  //       members_count: type === "band" && form.members_count ? Number(form.members_count) : null,
  //       youtube_url: form.youtube_url || null,
  //       instagram_url: form.instagram_url || null,
  //       soundcloud_url: form.soundcloud_url || null,
  //       whatsapp_number: form.whatsapp_number || null,
  //     });
  //     toast.success("Profile created");
  //     await refresh();
  //     nav("/profiles");
  //   } catch (e) {
  //     toast.error(e?.response?.data?.detail || "Failed");
  //   }
  // };

  const create = async () => {
  if (limitReached) {
    toast.error(`You already have a ${PROFILE_TYPE_LABEL[type]} profile. Delete it before creating another.`);
    return;
  }

  if (!form.display_name.trim()) {
    toast.error("Display name is required");
    return;
  }

  if (!location) {
    toast.error("Location is required");
    return;
  }

  if (!form.profile_picture_url) {
    toast.error("Profile picture is required");
    return;
  }

  if (!form.bio.trim()) {
    toast.error("Bio is required");
    return;
  }

  if (!form.whatsapp_number.trim()) {
    toast.error("Contact number is required");
    return;
  }

  const hasPortfolio =
    form.youtube_url.trim() ||
    form.instagram_url.trim() ||
    form.soundcloud_url.trim();

  if (type !== "venue" && !hasPortfolio) {
    toast.error("At least one portfolio link is required");
    return;
  }

  try {
    await api.post("/profiles", {
      profile_type: type,
      display_name: form.display_name.trim(),
      location_city: location.city,
      location_state: location.state,
      bio: form.bio.trim(),
      profile_picture_url: form.profile_picture_url,
      genres,
      instruments,
      gig_types: gigTypes,
      venue_type: type === "venue" ? form.venue_type : null,
      capacity: type === "venue" && form.capacity ? Number(form.capacity) : null,
      group_type: type === "band" ? (form.group_type || "Band") : null,
      members_count: type === "band" && form.members_count
        ? Number(form.members_count)
        : null,
      youtube_url: form.youtube_url.trim() || null,
      instagram_url: form.instagram_url.trim() || null,
      soundcloud_url: form.soundcloud_url.trim() || null,
      whatsapp_number: form.whatsapp_number.trim(),
    });

    toast.success("Profile created");
    await refresh();
    nav("/profiles");
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Failed");
  }
};

  if (availableTypes.length === 0) {
    return (
      <AppLayout>
        <Card className="p-6 border-slate-200 max-w-3xl">
          <h1 className="font-display text-2xl font-bold tracking-tight">All profile types are already created</h1>
          <p className="mt-2 text-sm text-slate-600">
            You can have one Musician, Band, DJ and Venue profile per account. Delete an existing profile before creating another of that type.
          </p>
          <Button onClick={() => nav("/profiles")} className="mt-5 bg-[#4F46E5] hover:bg-[#4338CA] text-white">
            Manage profiles
          </Button>
        </Card>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <h1 className="font-display text-3xl font-bold tracking-tight">Add a profile</h1>
      <Card className="mt-6 p-6 border-slate-200 max-w-3xl">
        <div>
          <Label>Profile type</Label>
          <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
            {PROFILE_TYPES.map((t) => {
              const n = profileCounts[t];
              const disabled = n >= MAX_PROFILES_PER_TYPE;
              return (
                <button key={t} type="button" onClick={() => !disabled && setType(t)} disabled={disabled}
                  data-testid={`type-${t}`}
                  className={`px-3 py-2 text-sm rounded-lg border transition-colors text-left ${
                    type === t ? "bg-[#0F172A] text-white border-[#0F172A]" :
                    disabled ? "bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed" :
                    "bg-white border-slate-200 hover:border-slate-400"
                  }`}>
                  <div className="font-semibold text-sm">{PROFILE_TYPE_LABEL[t]}</div>
                  <div className="text-[11px] mt-0.5 opacity-80">{n}/{MAX_PROFILES_PER_TYPE}</div>
                </button>
              );
            })}
          </div>
          {limitReached && (
            <div className="mt-2 text-sm text-rose-600" data-testid="limit-warning">
              You can create a maximum of {MAX_PROFILES_PER_TYPE} profiles of this type per account.
            </div>
          )}
        </div>

        <div className="mt-6">
          <Label>Profile picture <span className="text-rose-600">*</span></Label>
          <div className="mt-2">
            <ProfilePictureUpload value={form.profile_picture_url} onChange={(v) => setForm({ ...form, profile_picture_url: v })} />
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Display name <span className="text-rose-600">*</span></Label>
            <Input data-testid="pf-name" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
          </div>
          <div>
            <Label>Location <span className="text-rose-600">*</span></Label>
            <div className="mt-1">
              <LocationCombobox value={location} onChange={setLocation} testId="pf-location" />
            </div>
          </div>
        </div>

        {type === "venue" && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Venue type</Label>
              <select className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                value={form.venue_type} onChange={(e) => setForm({ ...form, venue_type: e.target.value })}>
                <option value="">Select</option>
                {VENUE_TYPES.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <Label>Capacity</Label>
              <Input type="number" min="0" value={form.capacity}
                onWheel={(e) => e.target.blur()}
                onChange={(e) => setForm({ ...form, capacity: e.target.value.replace(/\D/g, "") })} />
            </div>
          </div>
        )}

        {type === "band" && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Group type</Label>
              <select className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                value={form.group_type} onChange={(e) => setForm({ ...form, group_type: e.target.value })}>
                {BAND_GROUP_TYPES.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <Label>Number of members</Label>
              <Input type="number" min="0" value={form.members_count}
                onWheel={(e) => e.target.blur()}
                onChange={(e) => setForm({ ...form, members_count: e.target.value.replace(/\D/g, "") })} />
            </div>
          </div>
        )}

        {(type === "musician" || type === "band" || type === "dj") && (
          <>
            <div className="mt-6">
              <Label className="font-semibold">Genres</Label>
              <ChipGroup items={GENRES} selected={genres} onToggle={(v) => toggle(genres, setGenres, v)} />
            </div>
            {(type === "musician" || type === "band") && (
              <div className="mt-6">
                <Label className="font-semibold">Instruments</Label>
                <ChipGroup items={INSTRUMENTS} selected={instruments} onToggle={(v) => toggle(instruments, setInstruments, v)} />
              </div>
            )}
            <div className="mt-6">
              <Label className="font-semibold">Gig types you play</Label>
              <ChipGroup items={GIG_TYPES} selected={gigTypes} onToggle={(v) => toggle(gigTypes, setGigTypes, v)} />
            </div>
          </>
        )}

        <div className="mt-6">
          <Label>
            Bio <span className="text-rose-600">*</span>
          </Label>
          <Textarea rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })}
            placeholder="Tell potential clients about you." />
        </div>

        <div className="mt-6">
          <Label className="font-semibold">Portfolio links (optional)</Label>
          <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input placeholder="YouTube URL" value={form.youtube_url} onChange={(e) => setForm({ ...form, youtube_url: e.target.value })} />
            <Input placeholder="Instagram URL" value={form.instagram_url} onChange={(e) => setForm({ ...form, instagram_url: e.target.value })} />
            <Input placeholder="SoundCloud URL" value={form.soundcloud_url} onChange={(e) => setForm({ ...form, soundcloud_url: e.target.value })} />
          </div>
        </div>

        <div className="mt-6">
          <Label className="font-semibold">Contact WhatsApp (private)</Label>
          <p className="text-xs text-slate-500">Revealed only after an application is accepted.</p>
          <Input className="mt-2" placeholder="WhatsApp with country code, e.g. 919812345678"
            value={form.whatsapp_number} onChange={(e) =>
  setForm({
    ...form,
    whatsapp_number: e.target.value.replace(/\D/g, ""),
  })
}
            data-testid="pf-whatsapp" />
        </div>

        <div className="mt-8 flex justify-end gap-3">
          <Button variant="ghost" onClick={() => nav(-1)}>Cancel</Button>
          <Button onClick={create} disabled={limitReached} data-testid="pf-submit"
            className="bg-[#4F46E5] hover:bg-[#4338CA] text-white">
            Create profile
          </Button>
        </div>
      </Card>
    </AppLayout>
  );
}
